import type {
  BuildRegulatorEvidencePackInput,
  PackDocument,
  PackPdfRenderer,
  PackRegisterRow,
  PackSection,
} from './types';

/** Fixed limits keep evidence assemblies bounded; detail remains linked by record ID. */
export const PACK_CAPS = {
  maxSections: 32,
  maxItems: 250,
  maxCanonicalBytes: 256 * 1024,
  maxPages: 42,
} as const;

/**
 * Fixed fallback mappings for registers whose schema has no Standards tag.
 * They are intentionally narrow: a record tag always remains a RECORD_TAG;
 * only these registers can be included by a documented REGISTER_DEFAULT.
 */
export const REGISTER_STANDARD_DEFAULTS = {
  complaints: [
    { standardKey: '2', outcomeKey: '2.6' },
    { standardKey: '6', outcomeKey: '6.1' },
  ],
  restrictive_practices: [{ standardKey: '5', outcomeKey: '5.1' }],
} as const;

function compare(value: string, other: string): number {
  return value < other ? -1 : value > other ? 1 : 0;
}

/** Canonical JSON makes persisted content digests reproducible across runtimes. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort(compare)
    .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
    .join(',')}}`;
}

function assertInputs(input: BuildRegulatorEvidencePackInput): void {
  const permitted = new Set(input.permittedFacilityIds);
  // A provider with no facilities still needs an honest organisation-wide pack.
  if (
    (input.facilityIds.length === 0 && permitted.size > 0) ||
    input.facilityIds.some((id) => !permitted.has(id))
  ) {
    throw new Error('pack_facility_scope_not_permitted');
  }
  if (input.registerRows.some((row) => !permitted.has(row.facilityId))) {
    // Reject rather than silently dropping a row: omission can conceal a boundary breach.
    throw new Error('pack_row_facility_scope_not_permitted');
  }
}

function scopedRows(
  rows: readonly PackRegisterRow[],
  facilities: readonly string[],
): PackRegisterRow[] {
  const selected = new Set(facilities);
  return rows.filter((row) => selected.has(row.facilityId));
}

function uniqueRows(rows: readonly PackRegisterRow[]): PackRegisterRow[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = `${row.register}:${row.recordId}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function rowMatchesOutcome(row: PackRegisterRow, standardKey: string, outcomeKey: string): boolean {
  if (!row.standardKeys.includes(standardKey)) return false;
  const outcomes = row.outcomeKeys ?? [];
  return outcomeKey === 'UNSPECIFIED' ? outcomes.length === 0 : outcomes.includes(outcomeKey);
}

function bareDocument(document: PackDocument): Omit<PackDocument, 'contentDigest'> {
  const { asOf: _asOf, ...digestManifest } = document.manifest;
  return {
    kind: document.kind,
    // `asOf` is display metadata, not source evidence; it must not change an
    // item/content digest when the identical scoped inputs are retried.
    manifest: digestManifest as PackDocument['manifest'],
    sections: document.sections,
  };
}

export function buildRegulatorEvidencePack(input: BuildRegulatorEvidencePackInput): PackDocument {
  assertInputs(input);
  const resolutions = [...input.resolverStatuses].sort((a, b) =>
    compare(a.obligationId, b.obligationId),
  );
  const statusByObligation = new Map(
    resolutions.map((resolution) => [resolution.obligationId, resolution.status]),
  );
  const omittedObligations = resolutions
    .filter((resolution) => resolution.status === 'DOES_NOT_APPLY')
    .map((resolution) => resolution.obligationId);
  const rows = uniqueRows(scopedRows(input.registerRows, input.facilityIds));
  const sections: PackSection[] = input.standardOutcomes
    .filter(
      (mapping) =>
        (mapping.applicability ?? statusByObligation.get(mapping.obligationId)) !==
        'DOES_NOT_APPLY',
    )
    .sort((a, b) => compare(a.standardKey, b.standardKey) || compare(a.outcomeKey, b.outcomeKey))
    .map((mapping) => {
      const citations = rows
        .filter((row) => rowMatchesOutcome(row, mapping.standardKey, mapping.outcomeKey))
        .sort((a, b) => compare(a.register, b.register) || compare(a.recordId, b.recordId))
        .map((row) => ({
          register: row.register,
          recordId: row.recordId,
          reference: row.reference,
          mapping: row.mapping,
          ...(row.provision ? { provision: row.provision } : {}),
        }));
      return {
        key: `${mapping.standardKey}:${mapping.outcomeKey}`,
        standardKey: mapping.standardKey,
        ...((mapping.applicability ?? statusByObligation.get(mapping.obligationId)) ===
        'UNKNOWN_SHOW_ANYWAY'
          ? { caveat: 'APPLICABILITY_UNKNOWN' as const }
          : {}),
        items: citations.map((citation) => ({
          key: `${citation.register}:${citation.recordId}`,
          outcomeKey: mapping.outcomeKey,
          citations: [citation],
        })),
      };
    })
    .filter((section) => section.items.length > 0);
  // A standard-level record has no defensible outcome assignment. Keep it in
  // the pack under an explicit unspecified outcome instead of relabelling it
  // as whichever outcome happened to be selected by the caller.
  const unspecified = new Map<string, PackRegisterRow[]>();
  for (const row of rows) {
    for (const standardKey of row.standardKeys) {
      if (
        !row.unspecifiedStandardKeys?.includes(standardKey) &&
        (row.outcomeKeys ?? []).some((outcome) => outcome.startsWith(`${standardKey}.`))
      )
        continue;
      const list = unspecified.get(standardKey) ?? [];
      list.push(row);
      unspecified.set(standardKey, list);
    }
  }
  for (const [standardKey, standardRows] of unspecified) {
    const mapping = input.standardOutcomes.find(
      (entry) =>
        entry.standardKey === standardKey &&
        (entry.applicability ?? statusByObligation.get(entry.obligationId)) !== 'DOES_NOT_APPLY',
    );
    if (
      !mapping ||
      mapping.applicability === 'DOES_NOT_APPLY' ||
      statusByObligation.get(mapping.obligationId) === 'DOES_NOT_APPLY'
    )
      continue;
    const citations = standardRows
      .sort((a, b) => compare(a.register, b.register) || compare(a.recordId, b.recordId))
      .map((row) => ({
        register: row.register,
        recordId: row.recordId,
        reference: row.reference,
        mapping: row.mapping,
        ...(row.provision ? { provision: row.provision } : {}),
      }));
    sections.push({
      key: `${standardKey}:UNSPECIFIED`,
      standardKey,
      ...(mapping.applicability === 'UNKNOWN_SHOW_ANYWAY' ||
      statusByObligation.get(mapping.obligationId) === 'UNKNOWN_SHOW_ANYWAY'
        ? { caveat: 'APPLICABILITY_UNKNOWN' as const }
        : {}),
      items: citations.map((citation) => ({
        key: `${citation.register}:${citation.recordId}`,
        outcomeKey: 'UNSPECIFIED',
        citations: [citation],
      })),
    });
  }
  if (input.screeningAttestations?.length) {
    const attestations = [...input.screeningAttestations].sort((left, right) =>
      compare(left.id, right.id),
    );
    if (new Set(attestations.map((attestation) => attestation.id)).size !== attestations.length)
      throw new Error('pack_duplicate_screening_attestation');
    sections.push({
      key: 'screening-attestation-only',
      standardKey: 'SCREENING',
      items: attestations.map((attestation) => ({
        key: `attestation:${attestation.id}`,
        outcomeKey: 'SCREENING_ATTESTATION',
        citations: [
          {
            register: 'SCREENING_ATTESTATION',
            recordId: attestation.id,
            reference: attestation.reference,
            mapping: 'REGISTER_DEFAULT',
          },
        ],
      })),
    });
  }
  const itemCount = sections.reduce((total, section) => total + section.items.length, 0);
  if (sections.length > PACK_CAPS.maxSections || itemCount > PACK_CAPS.maxItems) {
    throw new Error('pack_evidence_cap_exceeded');
  }
  const draft: PackDocument = {
    kind: 'REGULATOR_EVIDENCE_ASSEMBLY',
    manifest: {
      asOf: input.asOf,
      period: input.period,
      facilityIds: [...input.facilityIds].sort(compare),
      templateVersion: input.templateVersion,
      legalSourceVersion: input.legalSourceVersion,
      resolverSnapshot: resolutions,
      omittedObligations,
      caps: PACK_CAPS,
    },
    sections,
    contentDigest: '',
  };
  const canonical = canonicalJson(bareDocument(draft));
  if (new TextEncoder().encode(canonical).byteLength > PACK_CAPS.maxCanonicalBytes) {
    throw new Error('pack_canonical_byte_cap_exceeded');
  }
  return { ...draft, contentDigest: input.sha256Hex(canonical) };
}

/** The renderer gets labels and IDs only; it cannot receive unbounded source prose. */
export function renderPackPdf(
  document: PackDocument,
  renderer: PackPdfRenderer,
  scope?: { standardKeys?: readonly string[]; facilityLabels?: readonly string[] },
): Uint8Array {
  const standardKeys = scope?.standardKeys ?? [
    ...new Set(document.sections.map((section) => section.standardKey)),
  ];
  const facilityLabels = scope?.facilityLabels ?? document.manifest.facilityIds;
  const citationCount = document.sections.reduce(
    (total, section) =>
      total + section.items.reduce((count, item) => count + item.citations.length, 0),
    0,
  );
  const lines = [
    'Statura Care regulator evidence assembly — not a regulator form',
    `Date range: ${document.manifest.period.start} to ${document.manifest.period.end}`,
    'Programme: Aged care quality standards',
    `Locations: ${facilityLabels.length ? facilityLabels.join(', ') : 'No facilities configured'}`,
    `Selected standards: ${standardKeys.length ? standardKeys.map((key) => `Standard ${key}`).join(', ') : 'Not recorded in export context'}`,
    `Contents: ${document.sections.length} sections; ${citationCount} evidence references`,
    ...(document.sections.length === 0
      ? [
          'No tagged evidence matches this scope. Applicability is not established by an empty export.',
        ]
      : []),
    `As of: ${document.manifest.asOf}`,
    `Digest: ${document.contentDigest}`,
    ...document.sections.flatMap((section) => [
      `${section.standardKey} / ${section.key}${section.caveat ? ' — applicability unconfirmed' : ''}`,
      ...section.items.map((item) =>
        item.citations
          .map((citation) => `${citation.register} | ${citation.reference} | ${citation.recordId}`)
          .join(' | '),
      ),
    ]),
  ];
  // Preserve the input cap without silently dropping identifiers from the export.
  const lineCap = PACK_CAPS.maxPages * 42;
  const boundedLines =
    lines.length > lineCap
      ? [
          ...lines.slice(0, lineCap - 1),
          `Export truncated: ${lines.length - lineCap + 1} source lines exceed the input cap.`,
        ]
      : lines;
  return renderer.render(boundedLines, PACK_CAPS.maxPages);
}

export function renderPackCsv(document: PackDocument): string {
  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const rows = ['section_key,item_key,outcome_key,register,record_id,reference,provision,mapping'];
  for (const section of document.sections) {
    for (const item of section.items) {
      for (const citation of item.citations) {
        rows.push(
          [
            section.key,
            item.key,
            item.outcomeKey,
            citation.register,
            citation.recordId,
            citation.reference,
            citation.provision ?? '',
            citation.mapping,
          ]
            .map(escape)
            .join(','),
        );
      }
    }
  }
  return `${rows.join('\n')}\n`;
}
